import { router, useForm } from '@inertiajs/react'
import { 
  Container,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  FormHelperText,
  SelectChangeEvent,
  Divider,
  Tooltip,
  IconButton,
  Alert,
  InputAdornment,
  Link,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info';
import { useEffect, useState } from 'react';
import theme from './Theme';
import Recommendation from '../components/Recommendation';
import Layout from './Layout';
import { InfoOutlined } from '@mui/icons-material';
import { inputBaseClasses } from '@mui/material/InputBase';
interface MedicationMatch {
  birthdate?: string;
  age?: number;
  race?: string;
  weight?: number;
  height?: number;
  bmi?: number;
  skipPeriod?: boolean;
  hasAcne?: boolean;
  hasPCOS?: boolean;
  hasMecContraindication?: boolean;
  payingCash?: boolean;
}

interface MedicationRecommendation {
  medication_name: string;
  medication_family: string;
  progestin: string;
  ethinyl_estradiol: string;
  notes: string[];
}

export default function NewMedicationMatch({
  match,
  recommendations
}: {
  match?: MedicationMatch;
  recommendations?: MedicationRecommendation[];
}) {

  const initialBirthdate = match?.birthdate;
  const initialAge = match?.age;
  const initialRace = match?.race;
  const initialWeight = match?.weight;
  const initialHeight = match?.height;
  const initialBmi = match?.bmi;
  const initialskipPeriod = match?.skipPeriod;
  const initialHasAcne = match?.hasAcne;
  const initialHasPCOS = match?.hasPCOS;
  const initialHasMecContraindication = match?.hasMecContraindication;
  const initialPayingCash = match?.payingCash;

  return (
    <Layout title="New Medication Match">
      <Container sx={{ 
        mt: 12, 
        display: 'flex', 
        justifyContent: 'center', 
        flexDirection: 'column',
        gap: 3,
        pb: 5,
        [theme.breakpoints.up('desktop')]: { flexDirection: 'row' }
      }}>
        <MedicationMatchForm
          initialBirthdate={initialBirthdate}
          initialAge={initialAge}
          initialBmi={initialBmi}
          initialRace={initialRace} 
          initialWeight={initialWeight}
          initialHeight={initialHeight}
          initialskipPeriod={initialskipPeriod}
          initialHasAcne={initialHasAcne}
          initialHasPCOS={initialHasPCOS}
          initialHasMecContraindication={initialHasMecContraindication}
          initialPayingCash={initialPayingCash}
        />
        {recommendations && recommendations.length > 0 && (
          <Paper elevation={6} sx={{ 
            gap: 2,
            minWidth: '272px',
            maxWidth: '685px',
            p: 3,
            background: 'url("/recommendation-bg.png") lightgray 0px 0px / 100% 100% no-repeat',
            height: 'fit-content',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.1 3.9L5.5 0L3.9 3.9L0 5.5L3.9 7.1L5.5 11L7.1 7.1L11 5.5L7.1 3.9Z" fill="#3455CD"/>
                <path d="M19.1 7.9L17.5 4L15.9 7.9L12 9.5L15.9 11.1L17.5 15L19.1 11.1L23 9.5L19.1 7.9Z" fill="#3455CD"/>
                <path d="M11.1 15.9L9.5 12L7.9 15.9L4 17.5L7.9 19.1L9.5 23L11.1 19.1L15 17.5L11.1 15.9Z" fill="#3455CD"/>
              </svg>
              <Typography variant="h6">
                Medication Recommendations
              </Typography>
            </Box>
            {recommendations.map((recommendation, index) => (
              <Recommendation recommendation={recommendation} tag={index === 0 ? "BEST" : "VALUE"} />
            ))}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: '64px',
                height: '65px',
                background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='65' viewBox='0 0 64 65' fill='none'%3E%3Cpath d='M21.2237 9.72433C14.8545 7.57166 14.362 17.4583 14.9119 22.6707C15.0317 27.6313 20.0826 38.6016 18.8279 45.1461C17.8242 50.3817 15.4078 48.4867 14.3251 46.8847C12.033 38.8184 6.24095 24.4178 1.41023 31.3456C-4.62816 40.0053 11.5948 54.9362 13.0766 56.5874C14.5584 58.2387 21.3272 63.1878 31.9617 64.5949C42.5961 66.002 50.1204 61.0991 56.8732 57.7768C63.626 54.4545 64.3937 50.9187 61.2231 49.7972C58.0525 48.6758 52.3548 51.4696 46.5829 52.3084C41.9654 52.9795 43.1519 50.0345 44.3224 48.4781C48.0927 46.6056 56.7646 38.0508 60.6292 34.0075C62.8318 32.1733 66.1512 27.7099 61.8078 24.5305C57.4645 21.3511 47.8157 34.4596 43.5342 41.4113C37.4019 45.2136 42.1778 31.7641 45.3322 24.564C48.1801 16.6126 51.7212 0.578797 43.1021 0.0549067C32.3283 -0.599957 32.0985 26.17 31.8989 32.186C31.6993 38.202 28.1216 44.9768 26.2192 44.3039C24.3169 43.631 24.3163 43.3303 25.5516 26.7848C26.5399 13.5485 23.0781 9.89602 21.2237 9.72433Z' fill='%23C0C9FF'/%3E%3C/svg%3E") no-repeat`,
                zIndex: 0
              }
            }}>
              <Box sx={{ gap: 2, display: 'flex', alignItems: 'center', zIndex: 1, pl: 2, mt: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '24px' }}>93%</Typography>
                <Typography variant="h6">
                  of patients are happy with their treatment plan*
                </Typography>
              </Box>
            </Box>
            <Typography pl={2} variant="body1" color="text.secondary">
              * 
              {'See how we collected this data '}
              <Link href="http://bc.pandiahealth.com/precision-matching-data" target="_blank" rel="noopener noreferrer">
                here
              </Link>
            </Typography>
          </Paper>
        )}
      </Container>
    </Layout>
  )
}

interface MedicationMatchFormProps {
  initialBirthdate?: string
  initialAge?: number
  initialRace?: string
  initialWeight?: number
  initialHeight?: number
  initialBmi?: number
  initialskipPeriod?: boolean
  initialHasAcne?: boolean
  initialHasPCOS?: boolean
  initialHasMecContraindication?: boolean
  initialPayingCash?: boolean
}

function MedicationMatchForm({
  initialBirthdate,
  initialAge,
  initialRace,
  initialWeight,
  initialHeight,
  initialBmi,
  initialskipPeriod,
  initialHasAcne,
  initialHasPCOS,
  initialHasMecContraindication,
  initialPayingCash,
}: MedicationMatchFormProps) {
  const { data, setData, clearErrors, setError, post, processing, errors, transform } = useForm({
    birthdate: initialBirthdate,
    age: initialAge,
    race: initialRace,
    weight: initialWeight,
    heightFeet: initialHeight ? Math.floor(initialHeight / 12) : undefined,
    heightInches: initialHeight ? initialHeight % 12 : undefined,
    bmi: initialBmi,
    skipPeriod: initialskipPeriod,
    hasAcne: initialHasAcne,
    hasPCOS: initialHasPCOS,
    hasMecContraindication: initialHasMecContraindication,
    payingCash: initialPayingCash
  })
  const [showInfoAlert, setShowInfoAlert] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    transform((data) => ({
      ...data,
      height: data.heightFeet ? data.heightFeet * 12 + (data.heightInches || 0) : undefined,
      skip_period: data.skipPeriod,
      has_acne: data.hasAcne,
      has_pcos: data.hasPCOS,
      paying_cash: data.payingCash
    }))
    post('/medication_match')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const name = e.target.name as string
    const value = e.target.value
    setData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  useEffect(() => {    
    if (data.weight && data.heightFeet && data.heightInches) {
      const height = data.heightFeet * 12 + data.heightInches;
      // BMI formula: (weight in pounds * 703) / (height in inches)^2
      const bmi = (data.weight * 703) / (height * height);
      setData(prev => ({
      ...prev,
        bmi: Math.round(bmi * 10) / 10 // Round to 1 decimal place
      }));
    }
  }, [data.heightFeet, data.heightInches, data.weight]);

  useEffect(() => {
    clearErrors('age');
    if (data.age && data.age < 10) {
      setError('age', 'Age must be at least 10 years old');
    }
  }, [data.age]);

  useEffect(() => {
    clearErrors('birthdate');

    if (data.birthdate) {
    const birthDate = new Date(data.birthdate);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();

      if (age < 0) {
        setError('birthdate', 'Invalid date: Birthdate cannot be in the future');
      }
      else {
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        setData(prev => ({...prev, age: age}));
      }
    }
  }, [data.birthdate]);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{[theme.breakpoints.up('desktop')]: { minWidth: '448px' }, flex: 1, maxWidth: '685px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center'  }}>
        <Tooltip title="Click for more information">
          <IconButton onClick={() => setShowInfoAlert(true)} sx={{ pl: 0,  display: showInfoAlert ? 'none' : 'block' }}>
            <InfoOutlined sx={{ color: theme.palette.info.main }} />
          </IconButton>
        </Tooltip>
        {showInfoAlert && (
          <Alert 
            severity="info"
            variant="outlined"
            sx={{ mb: 2, borderRadius: '12px', backgroundColor: 'white' }}
            onClose={() => setShowInfoAlert(false)}
          >
            <Typography variant="subtitle1">
              The Precision Matching Tool uses advanced algorithms and clinical data to deliver personalized, evidence-based birth control recommendations tailored to each patient.
              Enter the information below to get started.
            </Typography> 
          </Alert>
        )}
      </Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Patient Information </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3}}>
        <Paper elevation={0} sx={{ display: 'flex', alignSelf: 'stretch', flexDirection: 'column', gap: 1, p: 1.5, backgroundColor: theme.pandia.blue}}>
          <Typography variant="body2" gutterBottom>Enter the patient's date of birth and the age field will fill automatically. </Typography>
          <Box sx={{ display: 'flex', gap: 3, [theme.breakpoints.down('tablet')]: { flexDirection: 'column' }}}>
            <TextField
              sx={{ flex: 1, backgroundColor: 'white'}}
              type="date"
              id="birthdate"
              name="birthdate"
              label="Date of Birth"
              value={data.birthdate}
              onChange={(e) => {
                setData(prev => ({
                  ...prev,
                  birthdate: e.target.value,
                }));
              }}
              error={!!errors.birthdate}
              helperText={errors.birthdate}
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />

            
            <TextField
              sx={{ flex: 1, backgroundColor: 'white'}}
              id="age"
              name="age"
              value={data.age}
              label={<><span style={{ color: theme.palette.error.main }}>* </span><span>Age</span></>}
              onChange={(e) => {
                const age = parseInt(e.target.value) || undefined;
                setData(prev => ({
                  ...prev,
                  birthdate: '',
                  age: age
                }));
              }}
              error={!!errors.age}
              helperText={errors.age}
              slotProps={{
                inputLabel: {
                  shrink: !!data.age
                },
                input: {
                  endAdornment: <Tooltip title="Automatically calculated based on the date of birth.">
                    <IconButton>
                      <InfoIcon sx={{ color: theme.pandia.indigo }} />
                    </IconButton>
                  </Tooltip>
                },
                htmlInput: { min: 0, max: 120 }
              }}
            />
          </Box>
        </Paper>


        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', alignSelf: 'stretch', [theme.breakpoints.down('tablet_wide')]: { flexDirection: 'column' }}}>
          <Box sx={{ display: 'flex', flex: 3, gap: 2, alignItems: 'flex-start', alignSelf: 'stretch', [theme.breakpoints.down('tablet')]: { flexDirection: 'column' }}}>
            <Box sx={{ flex: 1, display: 'flex', gap: 1, alignSelf: 'stretch'}}>
              <FormControl sx={{ flex: 1 }} error={!!errors.heightFeet}>
                <InputLabel id="height-label"><span style={{ color: theme.palette.error.main }}>* </span> Height (ft)</InputLabel>
                <Select
                  labelId="height-label"
                  id="heightFeet" 
                  name="heightFeet"
                  value={data.heightFeet?.toString() || ''}
                  onChange={handleChange}
                  label="Height (ft)"
                >
                  <MenuItem value={4}>4 ft</MenuItem>
                  <MenuItem value={5}>5 ft</MenuItem>
                  <MenuItem value={6}>6 ft</MenuItem>
                  <MenuItem value={7}>7 ft</MenuItem>
                  <MenuItem value={8}>8 ft</MenuItem>
                </Select>
                {errors.heightFeet && <FormHelperText>{errors.heightFeet}</FormHelperText>}
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', gap: 1, alignSelf: 'stretch'}}>
              <FormControl sx={{ flex: 1 }} error={!!errors.heightInches}>
                <InputLabel id="height-label"><span style={{ color: theme.palette.error.main }}>* </span> Height (in)</InputLabel>
                <Select
                  labelId="height-label"
                  id="heightInches"
                  name="heightInches"
                  value={data.heightInches?.toString() || ''}
                  onChange={handleChange}
                  label="Height (in)"
                >
                  <MenuItem value={0}>0 in</MenuItem>
                  <MenuItem value={1}>1 in</MenuItem>
                  <MenuItem value={2}>2 in</MenuItem>
                  <MenuItem value={3}>3 in</MenuItem>
                  <MenuItem value={4}>4 in</MenuItem>
                  <MenuItem value={5}>5 in</MenuItem>
                  <MenuItem value={6}>6 in</MenuItem>
                  <MenuItem value={7}>7 in</MenuItem>
                  <MenuItem value={8}>8 in</MenuItem>
                  <MenuItem value={9}>9 in</MenuItem>
                  <MenuItem value={10}>10 in</MenuItem>
                  <MenuItem value={11}>11 in</MenuItem>
                </Select>
                {errors.heightInches && <FormHelperText>{errors.heightInches}</FormHelperText>}
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', gap: 1, alignSelf: 'stretch'}}>
              <FormControl sx={{ flex: 1 }} error={!!errors.weight}>
                <InputLabel id="weight-label"><span style={{ color: theme.palette.error.main }}>* </span>Weight (lbs)</InputLabel>
                <Select
                  labelId="weight-label"
                  id="weight"
                  name="weight"
                  required
                  value={data.weight?.toString() || ''}
                  onChange={handleChange}
                  label="Weight (lbs)"
                >
                  {Array.from({ length: 85 }, (_, i) => 80 + i * 5).map((weight) => (
                    <MenuItem key={weight} value={weight}>{weight} lbs</MenuItem>
                  ))}
                </Select>
                {errors.weight && <FormHelperText>{errors.weight}</FormHelperText>}
              </FormControl>
            </Box>
          </Box>
          <Divider orientation={theme.breakpoints.up('tablet_wide') ? "vertical" : "horizontal"} flexItem />
          <TextField
            sx={{ 
              flex: 1, 
              alignSelf: 'stretch',
              color: 'black',
              [theme.breakpoints.down('tablet_wide')]: {
                width: '100%',
              }
            }}
            id="bmi"
            name="bmi" 
            disabled
            label="BMI"
            value={data.bmi?.toString() || ''}
            slotProps={{
              inputLabel: {
                shrink: !!data.bmi,
              },
              input: {
                endAdornment: <Tooltip arrow placement='bottom' title="Automatically calculated based on the height and weight.">
                  <IconButton sx={{ padding: 0 }}>
                    <InfoIcon sx={{ color: theme.pandia.indigo }} />
                  </IconButton>
                </Tooltip>
              }
            }}
          >
          </TextField>
        </Box>

        <FormControl fullWidth error={!!errors.race}>
          <InputLabel id="skipPeriod-label"><span style={{ color: theme.palette.error.main }}>* </span> Period Preference</InputLabel>
          <Select
            id="skipPeriod"
            name="skipPeriod"
            value={data.skipPeriod?.toString()}
            onChange={handleChange}
            labelId="skipPeriod-label"
            label="* Period Preference"
          >
            <MenuItem value="true">Skipping</MenuItem>
            <MenuItem value="false">Not skipping</MenuItem>
          </Select>
          {errors.skipPeriod && <FormHelperText>{errors.skipPeriod}</FormHelperText>}
        </FormControl>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h6">Additional Information </Typography>
          <FormControl fullWidth error={!!errors.race}>
            <InputLabel id="race-label" shrink={!!data.race} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {data.race ? 'Race' : ''}
              {data.race && <Tooltip title="We use race as a proxy for genetics which can help improve recommendation accuracy.">
                <IconButton sx={{ padding: 0 }}>
                  <InfoIcon sx={{ color: theme.pandia.indigo }} />
                </IconButton>
              </Tooltip>}
            </InputLabel>
            <Select
              labelId="race-label"
              id="race"
              name="race"
              value={data.race}
              onChange={handleChange}
              label="Race"
              notched={!!data.race}
              startAdornment={<InputAdornment 
                position="start" 
                sx={{ [`[data-shrink=true] ~ .${inputBaseClasses.root} > &`]: { display: 'none', } }}
              >
                Race 
                <Tooltip title="We use race as a proxy for genetics which can help improve recommendation accuracy.">
                  <InfoIcon sx={{ color: theme.pandia.indigo }} />
                </Tooltip>
              </InputAdornment>}
            >
              <MenuItem value="">Select Race</MenuItem>
              <MenuItem value="asian">Asian</MenuItem>
              <MenuItem value="black">Black</MenuItem>
              <MenuItem value="hispanic">Hispanic</MenuItem>
              <MenuItem value="white">White</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
            {errors.race && <FormHelperText>{errors.race}</FormHelperText>}
          </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 1 }} error={!!errors.hasAcne}>
                <InputLabel id="hasAcne-label">Has Acne?</InputLabel>
                <Select
                  labelId="hasAcne-label"
                  id="hasAcne"
                  name="hasAcne"
                    value={data.hasAcne?.toString()}
                  onChange={handleChange}
                  label="Has Acne?"
                >
                  <MenuItem value={undefined}>Select</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
                {errors.hasAcne && <FormHelperText>{errors.hasAcne}</FormHelperText>}
              </FormControl>

              <FormControl sx={{ flex: 1 }} error={!!errors.hasPCOS}>
                <InputLabel id="hasPCOS-label">Has PCOS?</InputLabel>
                <Select
                  labelId="hasPCOS-label"
                  id="hasPCOS"
                  name="hasPCOS"
                  value={data.hasPCOS?.toString()}
                  onChange={handleChange}
                  label="Has PCOS?"
                >
                  <MenuItem value={undefined}>Select</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
                {errors.hasPCOS && <FormHelperText>{errors.hasPCOS}</FormHelperText>}
              </FormControl>
            </Box>

            <FormControl fullWidth error={!!errors.payingCash}>
              <InputLabel id="payingCash-label" shrink={!!data.payingCash} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {data.payingCash ? 'Payment preference' : ''}
                {data.payingCash && <Tooltip title="If a patient is paying out of pocket, our algorithm will adjust
                  the rx recommendations based on price point in addition to our other fields.">
                  <IconButton sx={{ padding: 0 }}>
                    <InfoIcon sx={{ color: theme.pandia.indigo }} />
                  </IconButton>
                </Tooltip>}
              </InputLabel>
              <Select
                labelId="payingCash-label"
                id="payingCash"
                name="payingCash"
                value={data.payingCash?.toString()}
                onChange={handleChange}
                notched={!!data.payingCash}
                label="Payment preference"
                startAdornment={<InputAdornment 
                  position="start" 
                  sx={{ [`[data-shrink=true] ~ .${inputBaseClasses.root} > &`]: { display: 'none', } }}
                >
                  Payment preference
                  <Tooltip title="If a patient is paying out of pocket, our algorithm will adjust
                  the rx recommendations based on price point in addition to our other fields.">
                    <InfoIcon sx={{ color: theme.pandia.indigo }} />
                  </Tooltip>
                </InputAdornment>}
              >
                <MenuItem value="true">Cash</MenuItem>
                <MenuItem value="false">Insurance</MenuItem>
              </Select>
              {errors.payingCash && <FormHelperText>{errors.payingCash}</FormHelperText>}
            </FormControl>
          </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <Button
            color="inherit"
            variant="text"
            size="large"
            onClick={() => router.visit('/')}
          >
            Clear
          </Button>
          <Button 
            type="submit"
            color="primary"
            variant="contained"
            size="large"
            disabled={processing || !data.age || !data.bmi || !data.skipPeriod}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
